import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import {
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as React from "react";

import {
  type AgentFeedbackRecord,
  FEEDBACK_CATEGORY_LABELS,
  FeedbackSentiment,
} from "@/shared/domain/chat";

import styles from "./adminTable.module.css";

function renderAdminLabelChip(row: AgentFeedbackRecord) {
  // Text: admin_label when non-empty, otherwise the resolved/open state word.
  // ``||`` (not ``??``) so a cleared-to-empty-string label falls back to the
  // state word — clearing an admin label should visibly revert to "open".
  // Colour: green when resolved, orange when open — carries the signal so the
  // text never needs to combine "resolved · label" redundantly.
  const label = row.admin_label || (row.resolved ? "resolved" : "open");
  return (
    <Chip
      label={label}
      size="small"
      color={row.resolved ? "success" : "warning"}
    />
  );
}

interface FeedbackTableProps {
  rows: AgentFeedbackRecord[];
  isLoading: boolean;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onNextPage: () => void;
  onPreviousPage: () => void;
  currentPage: number;
  onSelectFeedback: (feedback: AgentFeedbackRecord) => void;
  selectedFeedbackId?: string;
}

export function FeedbackTable({
  rows,
  isLoading,
  hasNextPage,
  hasPreviousPage,
  onNextPage,
  onPreviousPage,
  currentPage,
  onSelectFeedback,
  selectedFeedbackId,
}: FeedbackTableProps) {
  const theme = useTheme();

  if (isLoading && rows.length === 0) {
    return (
      <div className={styles.emptyState}>
        <CircularProgress size="1.5rem" />
      </div>
    );
  }

  if (rows.length === 0) {
    // Surface the empty state on every page, not just the first. The
    // previous ``!hasPreviousPage`` gate left an empty page-N rendering as
    // a blank table with no message — confusing when a filter narrows the
    // result set after the user has already paged forward.
    return (
      <div className={styles.emptyState}>
        {hasPreviousPage
          ? "No feedback on this page. Use Previous to go back."
          : "No feedback found."}
      </div>
    );
  }

  return (
    <div>
      <TableContainer className={styles.tableContainer}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>Sentiment</TableCell>
              <TableCell>Categories</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Org</TableCell>
              <TableCell>Session</TableCell>
              <TableCell>Msg #</TableCell>
              <TableCell>Admin label</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const isSelected = row.feedback_id === selectedFeedbackId;
              // Match the enum value rather than fall through "anything
              // not POSITIVE → thumbs down". If FeedbackSentiment grows a
              // ``NEUTRAL`` (or similar), the explicit checks make the
              // missing branch a TS warning rather than a silent thumbs-down.
              let sentimentIcon: React.ReactNode = null;
              if (row.sentiment === FeedbackSentiment.POSITIVE) {
                sentimentIcon = (
                  <ThumbUpAltIcon
                    fontSize="inherit"
                    sx={{ color: theme.palette.success.main }}
                  />
                );
              } else if (row.sentiment === FeedbackSentiment.NEGATIVE) {
                sentimentIcon = (
                  <ThumbDownAltIcon
                    fontSize="inherit"
                    sx={{ color: theme.palette.error.main }}
                  />
                );
              }
              return (
                <TableRow
                  key={row.feedback_id}
                  onClick={() => onSelectFeedback(row)}
                  className={`${styles.clickableRow} ${isSelected ? styles.selectedRow : ""}`}
                >
                  <TableCell>
                    {new Date(row.created).toLocaleString()}
                  </TableCell>
                  <TableCell>{sentimentIcon}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {row.categories.map((c) => (
                        <Chip
                          key={c}
                          label={FEEDBACK_CATEGORY_LABELS[c]}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell
                    className={styles.cellTruncate}
                    title={row.notes ?? ""}
                  >
                    {row.notes ?? "-"}
                  </TableCell>
                  <TableCell className={styles.cellTruncate}>
                    {row.created_by}
                  </TableCell>
                  <TableCell className={styles.cellTruncate}>
                    {row.org_id}
                  </TableCell>
                  <TableCell className={styles.cellTruncate}>
                    {row.session_id}
                  </TableCell>
                  <TableCell>{row.message_sequence_num}</TableCell>
                  <TableCell>{renderAdminLabelChip(row)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <div className={styles.paginationContainer}>
        <span className={styles.pageIndicator}>Page {currentPage + 1}</span>
        <div className={styles.paginationButtons}>
          <IconButton
            size="small"
            onClick={onPreviousPage}
            disabled={!hasPreviousPage || isLoading}
            aria-label="Previous page"
          >
            <NavigateBeforeIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={onNextPage}
            disabled={!hasNextPage || isLoading}
            aria-label="Next page"
          >
            <NavigateNextIcon />
          </IconButton>
        </div>
      </div>
    </div>
  );
}
